import React, { useState, useEffect } from "react";
import { saveAs } from "file-saver";
import BackButton from "../BackButton/BackButton";
import SelectedReturn from "./SelectedReturn";
import styles from "./ReturnsList.module.css";
import { useFont } from "../../contexts/FontContext/FontContext";
import useShipments from "../../hooks/useShipments";
import axios from "axios";

const ReturnsList = ( { seeAllExchanges, setExchangeOptionSelected } ) => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const [isFocused, setIsFocused] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [filteredShipments, setFilteredShipments] = useState([]);
  const fontSchemes = useFont();
  const fontFamilyOne = fontSchemes.scheme_one;
  const [selectedReturn, setSelectedReturn] = useState(null);
  const { shipments, orders, loadingShipments, error, fetchShipments } = useShipments();
  const token = localStorage.getItem("authToken");

  const downloadCSV = () => {
    const csvHeaders = ["Order ID", "Date Purchased", "Label Cost", "Label Purchase Price", "Tracking Status"];
    const csvRows = [
      csvHeaders.join(","),
      ...filteredShipments.map((shipment) =>
        [
          shipment.order_id || "N/A",
          new Date(shipment.date_purchased).toLocaleDateString(),
          `$${shipment.label_cost}`,
          `$${shipment.label_purchase_price}`,
          shipment.tracking_status.replace(/_/g, " ").replace(/^\w/, (char) => char.toUpperCase()),
        ].join(",")
      ),
    ];
    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "returns.csv");
  };

  //request products so that details like product quantity can be shown on selectedReturn component
  //@return_token String unique identifier for a given return order
  const getProducts = async (return_token) => {
    try {
      const response = await axios.get(`${apiUrl}/api/get_products/${return_token}/?get_exchange_data=${seeAllExchanges}`,
          {
              method: "GET",
              headers: {
                  "Content-Type": "application/json",
                  "Authorization": `Bearer ${token}`,
              },
          }
      );
      return {
        products: response.data.data,
        new_items: response.data.new_items,
      };
    } catch (error) { 
      window.location.reload();
      console.error("Error requesting products:", error); 
    }
  }

  //reveal the selectedReturn view
  const showSelectedReturn = async (shipment) => {
    const return_token = shipment.token;
    try {
      const { products, new_items } = await getProducts(return_token);
      const selectedOrderData = {
          token: shipment.token,
          business_name: shipment.business_name,
          customer_name: shipment.customer_name,
          customer_email: shipment.customer_email,
          order_id: shipment.order_id,
          shipment_id: shipment.shipment_id,
          carrier_name: shipment.carrier_name,
          carrier_service: shipment.carrier_service,
          date_purchased: shipment.date_purchased,
          label_cost: shipment.label_cost,
          label_purchase_price: shipment.label_purchase_price,
          label_url: shipment.label_url,
          package_dimensions: shipment.package_dimensions,
          tracking_status: shipment.tracking_status,
          delivered_at: shipment.delivered_at,
          tracking_number: shipment.tracking_code,
          refund_status: shipment.refund_status,
          weight: shipment.weight,
          show_in_wms: shipment.show_in_wms,
          products,
          new_items,
      };
      setSelectedReturn(selectedOrderData);
    } catch (error) { console.log(`error setting selected return data ${error}`)}
  };

  useEffect(() => { fetchShipments(searchValue.trim() || "most_recent"); }, [searchValue]);

  //filter shipments so that we always filter by the value in the search input, and we only look at link_types that are exchanges if seeAllExchanges prop is true (we are in see all exchanges view)
  useEffect(() => {
    if (shipments) {
      const filtered = shipments.filter((shipment) => {
        const matchesSearch = searchValue === "" || (shipment.order_id && shipment.order_id.startsWith(searchValue));
        const matchesLinkType = seeAllExchanges ? shipment.link_type === "exchange" : (!shipment.link_type || shipment.link_type === "return");
        return matchesSearch && matchesLinkType;
      });
      setFilteredShipments(filtered);
    }
  }, [searchValue, shipments, seeAllExchanges]);

  const handleShipmentSearchChange = (e) => {
      const value = e.target.value;
      setSearchValue(value);
      if (value.trim() !== "") { fetchShipments(value); }  
      else { fetchShipments("most_recent"); }
  };

  return (
    <div className={`${styles.returns_list_container} ${fontFamilyOne}`}>
      {!selectedReturn && (
        <div className={styles.returns_list_search_bar_container}>
          <div className={styles.recent_returns_wrapper}>
            <p className={`titles ${styles.recent_returns} fade_in`}>{searchValue ? "Matching results" : `Recent ${seeAllExchanges ? "exchanges" : "returns"}`}</p>
          </div>
          <div className={`${styles.search_bar_wrapper} fade_in`}>
            <p className={`${styles.search_label}`}>Search</p>
            <div className={`${styles.search_bar} ${isFocused ? styles.focused : ""}`}>
              <input type="text" placeholder="Enter order #" className={`${styles.search_input}`} value={searchValue} onChange={handleShipmentSearchChange} onFocus={() => setIsFocused(true)} onBlur={() => setIsFocused(false)} />
            </div>
          </div>
        </div>
      )}
      <div className={styles.returns_container}>
        {selectedReturn && (<SelectedReturn data={selectedReturn} setSelectedReturn={setSelectedReturn} isExchange={seeAllExchanges} token={token} />)}
        {!selectedReturn && (
          <>
            <div className={`${styles.returns_list_box} fade_in`}>
              {loadingShipments ? (<img className={styles.returns_list_loader} src={`${process.env.PUBLIC_URL}/images/shipwheel_loader.gif`} alt="Loading..." />)
              : (
                <>
                  <div className={`${styles.columns_header}`}>
                    <p className={styles.column_title}>Date purchased</p>
                    <p className={styles.column_title}>Order id</p>
                    <p className={styles.column_title}>Label cost</p>
                    <p className={styles.column_title}>Tracking status</p>
                  </div>
                  <div className={styles.results_container}>
                    {filteredShipments.length > 0 ? (
                      filteredShipments.map((shipment, index) => (
                        <div key={index} className={`${styles.shipment_row} fade_in`} onClick={() => showSelectedReturn(shipment)}>
                          <p className={styles.shipment_data}>{new Date(shipment.date_purchased).toLocaleDateString()}</p>
                          <p className={styles.shipment_data}>{shipment.order_id || "N/A"}</p>
                          <p className={styles.shipment_data}>${shipment.label_cost}</p>
                          <p className={`${styles.shipment_data} ${styles[shipment.tracking_status]}`}>{shipment.tracking_status.replace(/_/g, " ").replace(/^\w/, (char) => char.toUpperCase())}</p>
                        </div>
                      ))
                    ) : (<p className={`${styles.no_results}`}>No results found</p>)}
                  </div>
                </>
              )}
            </div>
            <div className={`${styles.download_button}`} onClick={downloadCSV}>
                <p className={`${styles.download_button_text} `}>Download</p>
                <img className={styles.download_button_image} src={`${process.env.PUBLIC_URL}/images/csv_file.png`} alt="download_csv" />
            </div>
            <BackButton button_id={styles.see_all_returns_back_button} image_id={styles.see_all_returns_back_button_arrow} to={seeAllExchanges ? null : "home"} customBack={seeAllExchanges ? () => setExchangeOptionSelected(null) : null} />
          </>
        )}
        </div>
    </div>
  );
};

export default ReturnsList;
